import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useStore } from "./store";
import { Login } from "./screens/Login";
import { Home } from "./screens/Home";
import { Kid } from "./screens/Kid";
import { Review } from "./screens/Review";
import { Diary } from "./screens/Diary";
import { Rewards } from "./screens/Rewards";
import { Settings } from "./screens/Settings";
import { InstallGuide } from "./screens/InstallGuide";
import { IntroPlayer } from "./screens/IntroPlayer";
import { markIntroSeen, shouldShowIntro } from "./intro";
import { TabBar } from "./components/TabBar";
import type { Tab } from "./components/TabBar";

type TabId = "home" | "diary" | "rewards" | "settings";

const TABS: readonly Tab<TabId>[] = [
  { id: "home", label: "Home", emoji: "🏠" },
  { id: "diary", label: "Diary", emoji: "📖" },
  { id: "rewards", label: "Rewards", emoji: "🏆" },
  { id: "settings", label: "Settings", emoji: "⚙️" },
];

/** Routes by plain state: signed out shows Login, signed in shows the active tab. */
export function App() {
  const { auth, snapshot, logout, retryAuth } = useStore();
  const [tab, setTab] = useState<TabId>("home");
  // The kid open on the Home tab, if any. Any tab press goes back to the top level.
  const [kidId, setKidId] = useState<string | null>(null);
  // Whether that kid's evening review is open on top of their screen.
  const [reviewing, setReviewing] = useState(false);
  // The kids switched off in the Diary's filter. Kept across tab changes, so going back to the
  // Diary finds the same kids; opening it from a kid's screen shows just that kid.
  const [diaryHidden, setDiaryHidden] = useState<string[]>([]);
  // Opened from a kid's screen, the Diary starts on that kid's day; from the tab bar, as it was.
  const [diaryToday, setDiaryToday] = useState(false);
  // The install guide, opened from the Home banner or Settings, on top of whichever tab that was.
  const [installGuide, setInstallGuide] = useState(false);
  // The intro film, on top of everything, until it closes.
  const [intro, setIntro] = useState(false);
  const closeIntro = useCallback(() => {
    markIntroSeen();
    setIntro(false);
  }, []);
  // Once per device, the moment someone signed in first lands: before anything else, so it can
  // never cut into adding the kids. A layout effect, so Home is not painted for a frame first.
  const introChecked = useRef(false);
  useLayoutEffect(() => {
    if (auth !== "ready" || !snapshot || introChecked.current) return;
    introChecked.current = true;
    if (shouldShowIntro()) setIntro(true);
  }, [auth, snapshot]);
  const closeKid = useCallback(() => {
    setKidId(null);
    setReviewing(false);
  }, []);
  const closeReview = useCallback(() => {
    setReviewing(false);
    window.scrollTo(0, 0);
  }, []);

  function openKid(id: string) {
    setKidId(id);
    window.scrollTo(0, 0);
  }

  function openReview() {
    setReviewing(true);
    window.scrollTo(0, 0);
  }

  function openDiary(id: string) {
    setDiaryHidden((snapshot?.kids ?? []).filter((k) => k.id !== id).map((k) => k.id));
    changeTab("diary");
    setDiaryToday(true);
    window.scrollTo(0, 0);
  }

  function changeTab(id: TabId) {
    setDiaryToday(false);
    setKidId(null);
    setReviewing(false);
    setInstallGuide(false);
    setTab(id);
  }

  function openInstallGuide() {
    setInstallGuide(true);
    window.scrollTo(0, 0);
  }

  // Same markup as the splash in index.html, so the hand-over from HTML to React does not flicker.
  if (auth === "loading")
    return (
      <div className="splash">
        <img src="/logo.svg" alt="Pointz" width="240" height="75" />
      </div>
    );
  if (auth === "signed_out") return <Login />;
  if (auth === "offline") {
    // The session is kept: no signal on launch should never mean signing in again.
    return (
      <main className="screen screen--center">
        <p className="lead">Can't reach Pointz</p>
        <button className="button button--primary" type="button" onClick={retryAuth}>
          Retry
        </button>
      </main>
    );
  }
  if (!snapshot) {
    // Signed in but no snapshot: normally a flash while it loads. If it sticks, offer a way out
    // rather than hanging on the splash forever.
    return (
      <main className="screen screen--center">
        <p className="lead">Loading…</p>
        <button className="button button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </main>
    );
  }

  return (
    <>
      {/* inert while the intro plays, so focus and taps stay on the film. */}
      <div className="app" inert={intro}>
        {installGuide && <InstallGuide onBack={() => setInstallGuide(false)} />}
        {!installGuide && tab === "home" && kidId && reviewing && <Review kidId={kidId} onBack={closeReview} />}
        {!installGuide && tab === "home" && kidId && !reviewing && (
          <Kid kidId={kidId} onBack={closeKid} onReview={openReview} onDiary={() => openDiary(kidId)} />
        )}
        {!installGuide && tab === "home" && !kidId && (
          <Home onOpenSettings={() => changeTab("settings")} onOpenKid={openKid} onInstallGuide={openInstallGuide} />
        )}
        {!installGuide && tab === "diary" && <Diary hidden={diaryHidden} onHidden={setDiaryHidden} startOnToday={diaryToday} />}
        {!installGuide && tab === "rewards" && <Rewards />}
        {!installGuide && tab === "settings" && (
          <Settings onInstallGuide={openInstallGuide} onWatchIntro={() => setIntro(true)} />
        )}
        <TabBar tabs={TABS} active={tab} onChange={changeTab} />
      </div>
      {intro && <IntroPlayer onClose={closeIntro} />}
    </>
  );
}
